import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Download, FileText, HelpCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { downloadFile, formatApiError } from "../lib/api";
import { GuideScreenshots } from "../components/GuideScreenshots";

export default function HelpPage() {
  const [help, setHelp] = useState(null);
  const [view, setView] = useState("mine");
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/help", { params: view === "client" ? { audience: "client" } : {} })
      .then(({ data }) => setHelp(data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [view]);

  const download = async (guide) => {
    setBusy(guide.key);
    try {
      await downloadFile(`/help/guides/${guide.key}`,
        `EmployeesFirst-${guide.title.replace(/ /g, "-")}.pdf`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "The guide could not be generated.");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-10" data-testid="help-page">
      <div className="mb-10 flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="eyebrow mb-2 flex items-center gap-2">
            <HelpCircle size={13}/> Help centre
          </div>
          <h1
            className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tight"
            style={{ color: "var(--ink)" }}
          >
            How the portal works
          </h1>
          <p className="mt-4 max-w-2xl text-base" style={{ color: "var(--muted)" }}>
            Written for what your account can actually do. Everything here is also available as a
            branded PDF you can forward to colleagues.
          </p>
        </div>
        {help?.is_admin && (
          <div className="flex items-center gap-2" data-testid="help-view-toggle">
            <button
              onClick={() => setView("mine")}
              className="btn-ghost"
              style={view === "mine" ? { borderColor: "var(--ink)", color: "var(--ink)" } : {}}
              data-testid="help-view-admin"
            >
              Administrator view
            </button>
            <button
              onClick={() => setView("client")}
              className="btn-ghost"
              style={view === "client" ? { borderColor: "var(--ink)", color: "var(--ink)" } : {}}
              data-testid="help-view-client"
            >
              View as a client
            </button>
          </div>
        )}
      </div>

      {!help ? (
        <p className="text-sm" style={{ color: "var(--muted)" }} data-testid="help-loading">Loading…</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-8" data-testid="help-sections">
            {help.sections.map((section, index) => (
              <motion.section
                key={section.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(index * 0.04, 0.3) }}
                className="border p-6 lg:p-8"
                style={{ borderColor: "var(--border)", background: "var(--surface)" }}
                data-testid={`help-section-${section.id}`}
              >
                <h2
                  className="font-heading text-base md:text-lg font-black tracking-tight"
                  style={{ color: "var(--ink)" }}
                >
                  {section.title}
                </h2>
                <div className="mt-4 space-y-3">
                  {section.body?.map((paragraph, i) => (
                    <p key={i} className="text-sm md:text-base leading-relaxed" style={{ color: "var(--ink)" }}>
                      {paragraph}
                    </p>
                  ))}
                </div>
                {section.steps?.length > 0 && (
                  <div className="mt-6 border-t pt-5" style={{ borderColor: "var(--border)" }}>
                    <div className="eyebrow mb-3">Step by step</div>
                    <ol className="space-y-2">
                      {section.steps.map((text, i) => (
                        <li key={i} className="flex gap-3 text-sm" style={{ color: "var(--ink)" }}>
                          <span className="font-mono text-xs font-bold" style={{ color: "var(--accent)" }}>
                            {String(i + 1).padStart(2, "0")}
                          </span>
                          <span>{text}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </motion.section>
            ))}
          </div>

          <aside className="space-y-6">
            <div className="border p-6" style={{ borderColor: "var(--border)" }} data-testid="help-guides">
              <div className="eyebrow mb-4">Shareable PDF guides</div>
              <div className="space-y-3">
                {help.guides.map((guide) => (
                  <button
                    key={guide.key}
                    onClick={() => download(guide)}
                    disabled={Boolean(busy)}
                    className="w-full border p-4 text-left transition-colors hover:border-[var(--ink)]"
                    style={{ borderColor: "var(--border)" }}
                    data-testid={`help-download-${guide.key}`}
                  >
                    <div className="flex items-start gap-3">
                      {busy === guide.key
                        ? <Loader2 className="animate-spin mt-0.5" size={16}/>
                        : <FileText size={16} className="mt-0.5" style={{ color: "var(--accent)" }}/>}
                      <div>
                        <div className="text-sm font-bold" style={{ color: "var(--ink)" }}>{guide.title}</div>
                        <div className="mt-1 text-xs" style={{ color: "var(--muted)" }}>{guide.subtitle}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
              <p className="mt-4 flex items-center gap-2 text-xs" style={{ color: "var(--muted)" }}>
                <Download size={12}/> PDFs include screenshots and your support contact.
              </p>
            </div>

            <div className="border p-6" style={{ borderColor: "var(--border)" }} data-testid="help-contact">
              <div className="eyebrow mb-3">Still stuck?</div>              <p className="text-sm" style={{ color: "var(--ink)" }}>
                Email{" "}
                <a href="mailto:support@myemployeesfirst.com" className="underline underline-offset-4"
                   style={{ color: "var(--accent)" }}>
                  support@myemployeesfirst.com
                </a>{" "}
                with your username and what you were doing. Never include your password.
              </p>
            </div>

            {help.is_admin && <GuideScreenshots />}
          </aside>
        </div>
      )}
    </div>
  );
}
